import { useState, useEffect } from 'react'
import { useT } from '@/i18n'
import { api } from '@/services/api-client'
import { Store, Plus, ToggleLeft, ToggleRight, Trash2, Tag, Activity, ShoppingCart, Edit2, Percent, X, Check, Search, Filter, TrendingUp, ChevronDown, ChevronUp, Clock, DollarSign, MessageSquare, Gauge, Award, EyeOff } from 'lucide-react'

type Listing = {
  id: string
  provider_type?: string
  provider?: string
  model: string
  price_per_1k_input?: number
  price_per_1k_output?: number
  price_input?: number
  price_output?: number
  total_requests?: number
  is_active?: number
  earned?: number
  total_earned?: number
  is_own?: number
  sharing_mode?: string
  discount_percent?: number
  promotion_percent?: number
  usage_limit?: number
  api_plan_name?: string
  visibility_threshold?: number
}

type ApiRequest = {
  id: string
  user_name?: string
  provider_type: string
  model: string
  max_tokens?: number
  budget_cents?: number
  proposed_price_input?: number
  proposed_price_output?: number
  valid_from?: string
  valid_until?: string
  description?: string
  status: string
  counter_offer_price_input?: number
  counter_offer_price_output?: number
  counter_offer_by_name?: string
  counter_offer_status?: string
  is_own?: number
  created_at: string
}

type Tab = 'available' | 'my' | 'requests'

const PROVIDER_OPTIONS = [
  { value: '', label: 'All Providers' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'google', label: 'Google (Gemini)' },
  { value: 'xai', label: 'xAI (Grok)' },
  { value: 'deepseek', label: 'DeepSeek' },
  { value: 'mistral', label: 'Mistral AI' },
  { value: 'together', label: 'Together AI' },
  { value: 'groq', label: 'Groq' },
  { value: 'cohere', label: 'Cohere' },
  { value: 'perplexity', label: 'Perplexity' },
  { value: 'openrouter', label: 'OpenRouter' },
]

function formatPrice(dollars: number): string {
  if (dollars === 0) return '$0'
  if (dollars >= 1) return `$${dollars.toFixed(2)}`
  if (dollars >= 0.01) return `$${dollars.toFixed(4)}`
  return `$${dollars.toFixed(7)}`
}

export function MarketplaceView() {
  const t = useT()
  const [tab, setTab] = useState<Tab>('available')
  const [listings, setListings] = useState<Listing[]>([])
  const [myListings, setMyListings] = useState<Listing[]>([])
  const [apiRequests, setApiRequests] = useState<ApiRequest[]>([])
  const [showCreate, setShowCreate] = useState(false)
  const [showRequestForm, setShowRequestForm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [expandedRequest, setExpandedRequest] = useState<string | null>(null)
  const [filterProvider, setFilterProvider] = useState('')
  const [filterModel, setFilterModel] = useState('')
  const [filterMinAvailable, setFilterMinAvailable] = useState(0)
  const [editForm, setEditForm] = useState({ price_input: '', price_output: '', discount_percent: '', promotion_percent: '', usage_limit: '', api_plan_name: '', visibility_threshold: '' })
  const [counterForm, setCounterForm] = useState({ price_input: '', price_output: '' })

  const [form, setForm] = useState({
    provider: 'openai', model: '', price_input: '', price_output: '',
    sharing_mode: 'parallel' as 'parallel' | 'exclusive',
    usage_limit: '', api_plan_name: '', visibility_threshold: '',
  })

  const [requestForm, setRequestForm] = useState({
    provider_type: 'openai', model: '', max_tokens: '', budget_cents: '',
    proposed_price_input: '', proposed_price_output: '', valid_until: '', description: '',
  })

  const fetchListings = async () => {
    setLoading(true)
    try { setListings(await api.getLeasingListings(filterProvider || undefined, filterMinAvailable || undefined)) } catch { /* */ } finally { setLoading(false) }
  }
  const fetchMyListings = async () => {
    setLoading(true)
    try { setMyListings(await api.getMyListings()) } catch { /* */ } finally { setLoading(false) }
  }
  const fetchRequests = async () => {
    setLoading(true)
    try { setApiRequests(await api.getApiRequests()) } catch { /* */ } finally { setLoading(false) }
  }

  useEffect(() => {
    if (tab === 'available') fetchListings()
    else if (tab === 'my') fetchMyListings()
    else fetchRequests()
  }, [tab])

  useEffect(() => { if (tab === 'available') fetchListings() }, [filterProvider, filterMinAvailable])

  const filteredListings = filterModel
    ? listings.filter(l => (l.model || '').toLowerCase().includes(filterModel.toLowerCase()))
    : listings

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await api.createListing({ provider_type: form.provider, model: form.model,
        price_per_1k_input: parseFloat(form.price_input), price_per_1k_output: parseFloat(form.price_output),
        sharing_mode: form.sharing_mode,
        usage_limit: form.usage_limit ? parseInt(form.usage_limit) : undefined,
        api_plan_name: form.api_plan_name || undefined,
        visibility_threshold: form.visibility_threshold ? parseInt(form.visibility_threshold) : undefined })
      setForm({ provider: 'openai', model: '', price_input: '', price_output: '', sharing_mode: 'parallel', usage_limit: '', api_plan_name: '', visibility_threshold: '' })
      setShowCreate(false); fetchMyListings(); setTab('my')
    } catch { /* */ }
  }

  const handleCreateRequest = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await api.createApiRequest({
        provider_type: requestForm.provider_type, model: requestForm.model,
        max_tokens: requestForm.max_tokens ? parseInt(requestForm.max_tokens) : undefined,
        budget_cents: requestForm.budget_cents ? parseFloat(requestForm.budget_cents) : undefined,
        proposed_price_input: requestForm.proposed_price_input ? parseFloat(requestForm.proposed_price_input) : undefined,
        proposed_price_output: requestForm.proposed_price_output ? parseFloat(requestForm.proposed_price_output) : undefined,
        valid_until: requestForm.valid_until || undefined, description: requestForm.description || undefined,
      })
      setRequestForm({ provider_type: 'openai', model: '', max_tokens: '', budget_cents: '', proposed_price_input: '', proposed_price_output: '', valid_until: '', description: '' })
      setShowRequestForm(false); fetchRequests()
    } catch { /* */ }
  }

  const handleToggle = async (l: Listing) => { try { await api.updateListing(l.id, { is_active: !l.is_active }); fetchMyListings() } catch { /* */ } }
  const handleDelete = async (id: string) => { try { await api.deleteListing(id); fetchMyListings() } catch { /* */ } }

  const startEdit = (l: Listing) => {
    setEditingId(l.id)
    setEditForm({
      price_input: String(l.price_per_1k_input || l.price_input || 0),
      price_output: String(l.price_per_1k_output || l.price_output || 0),
      discount_percent: String(l.discount_percent || 0),
      promotion_percent: String(l.promotion_percent || 0),
      usage_limit: String(l.usage_limit || 0),
      api_plan_name: l.api_plan_name || '',
      visibility_threshold: String(l.visibility_threshold || 0),
    })
  }

  const handleSaveEdit = async (id: string) => {
    try {
      await api.updateListing(id, {
        price_per_1k_input: parseFloat(editForm.price_input), price_per_1k_output: parseFloat(editForm.price_output),
        discount_percent: parseInt(editForm.discount_percent) || 0, promotion_percent: parseInt(editForm.promotion_percent) || 0,
        usage_limit: parseInt(editForm.usage_limit) || 0, api_plan_name: editForm.api_plan_name,
        visibility_threshold: parseInt(editForm.visibility_threshold) || 0,
      })
      setEditingId(null); fetchMyListings()
    } catch { /* */ }
  }

  const handleAcceptRequest = async (id: string) => { try { await api.acceptApiRequest(id); fetchRequests() } catch { /* */ } }
  const handleCounterOffer = async (id: string) => {
    try {
      await api.counterOfferApiRequest(id, { price_input: parseFloat(counterForm.price_input), price_output: parseFloat(counterForm.price_output) })
      setExpandedRequest(null); setCounterForm({ price_input: '', price_output: '' }); fetchRequests()
    } catch { /* */ }
  }
  const handleCancelRequest = async (id: string) => { try { await api.cancelApiRequest(id); fetchRequests() } catch { /* */ } }

  const getProvider = (l: Listing) => l.provider_type || l.provider || ''
  const getInputPrice = (l: Listing) => l.price_per_1k_input || l.price_input || 0
  const getOutputPrice = (l: Listing) => l.price_per_1k_output || l.price_output || 0
  const getEarned = (l: Listing) => l.total_earned || l.earned || 0
  const calcDiscount = (price: number, disc: number) => disc > 0 ? Math.round(price * (1 - disc / 100)) : price
  const getUsagePercent = (l: Listing) => {
    if (!l.usage_limit || l.usage_limit <= 0) return null
    return Math.min(100, Math.round(((l.total_requests || 0) / l.usage_limit) * 100))
  }

  return (
    <div className="marketplace">
      <div className="marketplace-header">
        <div className="marketplace-title-row">
          <Store size={24} />
          <div>
            <h1>{t.leasingMarketplace}</h1>
            <p className="marketplace-desc">{t.leasingMarketplaceDesc}</p>
          </div>
        </div>
        <button className="marketplace-create-btn" onClick={() => { setShowCreate(!showCreate); setShowRequestForm(false) }}>
          <Plus size={16} /> {t.leasingCreateListing}
        </button>
      </div>

      {showCreate && (
        <form className="marketplace-create-form" onSubmit={handleCreate}>
          <div className="marketplace-form-grid">
            <label className="marketplace-field">
              <span><Tag size={14} /> {t.leasingProvider}</span>
              <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}>
                {PROVIDER_OPTIONS.filter(p => p.value).map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </label>
            <label className="marketplace-field">
              <span><Activity size={14} /> {t.leasingModel}</span>
              <input type="text" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="gpt-4o, claude-sonnet-4-6..." required />
            </label>
            <label className="marketplace-field">
              <span>{t.leasingPriceInput} ($/1K)</span>
              <input type="number" step="0.0000001" min="0" value={form.price_input} onChange={(e) => setForm({ ...form, price_input: e.target.value })} placeholder="0" required />
            </label>
            <label className="marketplace-field">
              <span>{t.leasingPriceOutput} ($/1K)</span>
              <input type="number" step="0.0000001" min="0" value={form.price_output} onChange={(e) => setForm({ ...form, price_output: e.target.value })} placeholder="0" required />
            </label>
            <label className="marketplace-field">
              <span><Gauge size={14} /> Usage limit</span>
              <input type="number" min="0" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} placeholder="0 = unlimited" />
            </label>
            <label className="marketplace-field">
              <span><EyeOff size={14} /> Auto-hide below (%)</span>
              <input type="number" min="0" max="100" value={form.visibility_threshold} onChange={(e) => setForm({ ...form, visibility_threshold: e.target.value })} placeholder="0 = always visible" />
            </label>
            <label className="marketplace-field">
              <span><Award size={14} /> API Plan</span>
              <input type="text" value={form.api_plan_name} onChange={(e) => setForm({ ...form, api_plan_name: e.target.value })} placeholder="e.g. Pro, Enterprise..." />
            </label>
            <label className="marketplace-field marketplace-field--wide">
              <span>{t.leasingSharingMode}</span>
              <div className="marketplace-sharing-options">
                <label className={`marketplace-sharing-option ${form.sharing_mode === 'parallel' ? 'active' : ''}`}>
                  <input type="radio" name="sharing_mode" value="parallel" checked={form.sharing_mode === 'parallel'} onChange={() => setForm({ ...form, sharing_mode: 'parallel' })} />
                  <div><strong>{t.leasingParallel}</strong><span>{t.leasingParallelDesc}</span></div>
                </label>
                <label className={`marketplace-sharing-option ${form.sharing_mode === 'exclusive' ? 'active' : ''}`}>
                  <input type="radio" name="sharing_mode" value="exclusive" checked={form.sharing_mode === 'exclusive'} onChange={() => setForm({ ...form, sharing_mode: 'exclusive' })} />
                  <div><strong>{t.leasingExclusive}</strong><span>{t.leasingExclusiveDesc}</span></div>
                </label>
              </div>
            </label>
          </div>
          <button type="submit" className="marketplace-submit-btn"><Plus size={14} /> {t.leasingCreateListing}</button>
        </form>
      )}

      <div className="marketplace-tabs">
        <button className={`marketplace-tab ${tab === 'available' ? 'active' : ''}`} onClick={() => setTab('available')}>{t.leasingAvailable}</button>
        <button className={`marketplace-tab ${tab === 'my' ? 'active' : ''}`} onClick={() => setTab('my')}>{t.leasingMyListings}</button>
        <button className={`marketplace-tab ${tab === 'requests' ? 'active' : ''}`} onClick={() => setTab('requests')}>
          {t.leasingLookingForApi || 'Looking for API'}
        </button>
      </div>

      {tab === 'available' && (
        <div className="marketplace-filter-bar">
          <div className="marketplace-filter-item">
            <Filter size={14} />
            <select value={filterProvider} onChange={(e) => setFilterProvider(e.target.value)}>
              {PROVIDER_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div className="marketplace-filter-item">
            <Search size={14} />
            <input type="text" placeholder="Filter model..." value={filterModel} onChange={(e) => setFilterModel(e.target.value)} />
          </div>
          <div className="marketplace-filter-item marketplace-filter-availability">
            <Gauge size={14} />
            <span className="marketplace-filter-label">Min free: {filterMinAvailable}%</span>
            <input type="range" min="0" max="100" step="5" value={filterMinAvailable} onChange={(e) => setFilterMinAvailable(parseInt(e.target.value))} />
          </div>
        </div>
      )}

      <div className="marketplace-content">
        {loading && <p className="marketplace-loading">{t.loading}...</p>}

        {/* AVAILABLE */}
        {!loading && tab === 'available' && (filteredListings.length === 0 ? (
          <p className="marketplace-empty">{t.leasingNoListings}</p>
        ) : (
          <div className="marketplace-grid">
            {filteredListings.map((l) => (
              <div key={l.id} className={`marketplace-card ${l.is_own ? 'marketplace-card--own' : ''} ${(l.promotion_percent || 0) > 0 ? 'marketplace-card--promoted' : ''}`}>
                {(l.promotion_percent || 0) > 0 && <div className="marketplace-promoted-badge"><TrendingUp size={10} /> Promoted</div>}
                <div className="marketplace-card-header">
                  <span className="marketplace-provider-badge">{getProvider(l)}</span>
                  <span className="marketplace-model">{l.model}</span>
                </div>
                {(l.discount_percent || 0) > 0 && <div className="marketplace-discount-strip"><Percent size={11} /> -{l.discount_percent}%</div>}
                <div className="marketplace-card-prices">
                  <div className="marketplace-price-col">
                    <span className="marketplace-price-label">Input</span>
                    <span className="marketplace-price-value">
                      {(l.discount_percent || 0) > 0 ? (<><s className="marketplace-old-price">{formatPrice(getInputPrice(l))}</s> <strong>{formatPrice(calcDiscount(getInputPrice(l), l.discount_percent || 0))}</strong></>) : <strong>{formatPrice(getInputPrice(l))}</strong>}
                      <span className="marketplace-price-unit">/1K</span>
                    </span>
                  </div>
                  <div className="marketplace-price-divider" />
                  <div className="marketplace-price-col">
                    <span className="marketplace-price-label">Output</span>
                    <span className="marketplace-price-value">
                      {(l.discount_percent || 0) > 0 ? (<><s className="marketplace-old-price">{formatPrice(getOutputPrice(l))}</s> <strong>{formatPrice(calcDiscount(getOutputPrice(l), l.discount_percent || 0))}</strong></>) : <strong>{formatPrice(getOutputPrice(l))}</strong>}
                      <span className="marketplace-price-unit">/1K</span>
                    </span>
                  </div>
                </div>
                {(l.api_plan_name || getUsagePercent(l) !== null) && (
                  <div className="marketplace-card-usage">
                    {l.api_plan_name && <span className="marketplace-plan-badge"><Award size={11} /> {l.api_plan_name}</span>}
                    {getUsagePercent(l) !== null && (() => {
                      const pct = getUsagePercent(l)!
                      const color = pct >= 90 ? 'var(--danger)' : pct >= 70 ? 'var(--warning)' : 'var(--success)'
                      return (
                        <div className="marketplace-usage-bar-wrap">
                          <div className="marketplace-usage-bar">
                            <div className="marketplace-usage-bar-fill" style={{ width: `${pct}%`, backgroundColor: color }} />
                          </div>
                          <span className="marketplace-usage-text" style={{ color }}>{pct}%</span>
                        </div>
                      )
                    })()}
                  </div>
                )}
                <div className="marketplace-card-footer">
                  <span className="marketplace-card-stat">{l.total_requests ?? 0} req</span>
                  {l.is_own ? <span className="marketplace-own-tag">{t.leasingYourOffer}</span> : <button className="marketplace-lease-btn"><ShoppingCart size={13} /> {t.leasingLease}</button>}
                </div>
              </div>
            ))}
          </div>
        ))}

        {/* MY LISTINGS */}
        {!loading && tab === 'my' && (myListings.length === 0 ? (
          <p className="marketplace-empty">{t.leasingNoMyListings}</p>
        ) : (
          <div className="marketplace-grid">
            {myListings.map((l) => (
              <div key={l.id} className="marketplace-card">
                <div className="marketplace-card-header">
                  <span className="marketplace-provider-badge">{getProvider(l)}</span>
                  <span className="marketplace-model">{l.model}</span>
                </div>
                {editingId === l.id ? (
                  <div className="marketplace-edit-form">
                    <label className="marketplace-edit-field"><span>{t.leasingPriceInput} ($/1K)</span>
                      <input type="number" step="0.0000001" min="0" value={editForm.price_input} onChange={(e) => setEditForm({ ...editForm, price_input: e.target.value })} /></label>
                    <label className="marketplace-edit-field"><span>{t.leasingPriceOutput} ($/1K)</span>
                      <input type="number" step="0.0000001" min="0" value={editForm.price_output} onChange={(e) => setEditForm({ ...editForm, price_output: e.target.value })} /></label>
                    <label className="marketplace-edit-field"><span><Percent size={12} /> {t.leasingDiscount} %</span>
                      <input type="number" min="0" max="99" value={editForm.discount_percent} onChange={(e) => setEditForm({ ...editForm, discount_percent: e.target.value })} /></label>
                    <label className="marketplace-edit-field"><span><TrendingUp size={12} /> {t.leasingPromotion || 'Promotion'} %</span>
                      <input type="number" min="0" max="50" value={editForm.promotion_percent} onChange={(e) => setEditForm({ ...editForm, promotion_percent: e.target.value })} /></label>
                    <label className="marketplace-edit-field"><span><Gauge size={12} /> Usage limit (requests)</span>
                      <input type="number" min="0" value={editForm.usage_limit} onChange={(e) => setEditForm({ ...editForm, usage_limit: e.target.value })} placeholder="0 = unlimited" /></label>
                    <label className="marketplace-edit-field"><span><EyeOff size={12} /> Auto-hide below (% free)</span>
                      <input type="number" min="0" max="100" value={editForm.visibility_threshold} onChange={(e) => setEditForm({ ...editForm, visibility_threshold: e.target.value })} placeholder="0 = always visible" /></label>
                    <label className="marketplace-edit-field"><span><Award size={12} /> API Plan</span>
                      <input type="text" value={editForm.api_plan_name} onChange={(e) => setEditForm({ ...editForm, api_plan_name: e.target.value })} placeholder="e.g. Pro, Enterprise, Pay-as-you-go" /></label>
                    <div className="marketplace-edit-actions">
                      <button className="button-primary" onClick={() => handleSaveEdit(l.id)}><Check size={14} /> {t.save}</button>
                      <button className="button-secondary" onClick={() => setEditingId(null)}><X size={14} /> {t.cancel}</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="marketplace-card-prices">
                      <div className="marketplace-price-col"><span className="marketplace-price-label">Input</span><span className="marketplace-price-value"><strong>{formatPrice(getInputPrice(l))}</strong><span className="marketplace-price-unit">/1K</span></span></div>
                      <div className="marketplace-price-divider" />
                      <div className="marketplace-price-col"><span className="marketplace-price-label">Output</span><span className="marketplace-price-value"><strong>{formatPrice(getOutputPrice(l))}</strong><span className="marketplace-price-unit">/1K</span></span></div>
                    </div>
                    <div className="marketplace-card-body">
                      {(l.discount_percent || 0) > 0 && <div className="marketplace-card-row"><span className="marketplace-label"><Percent size={12} /> {t.leasingDiscount}</span><span className="marketplace-discount-text">-{l.discount_percent}%</span></div>}
                      {(l.promotion_percent || 0) > 0 && <div className="marketplace-card-row"><span className="marketplace-label"><TrendingUp size={12} /> Boost</span><span className="marketplace-promo-text">+{l.promotion_percent}%</span></div>}
                      <div className="marketplace-card-row"><span className="marketplace-label">{t.leasingEarned}</span><span>{formatPrice(getEarned(l))}</span></div>
                      {(l.visibility_threshold || 0) > 0 && <div className="marketplace-card-row"><span className="marketplace-label"><EyeOff size={12} /> Auto-hide</span><span>&lt; {l.visibility_threshold}% free</span></div>}
                      {l.api_plan_name && <div className="marketplace-card-row"><span className="marketplace-label"><Award size={12} /> Plan</span><span>{l.api_plan_name}</span></div>}
                      {getUsagePercent(l) !== null && (() => {
                        const pct = getUsagePercent(l)!
                        const color = pct >= 90 ? 'var(--danger)' : pct >= 70 ? 'var(--warning)' : 'var(--success)'
                        return (
                          <div className="marketplace-card-row">
                            <span className="marketplace-label"><Gauge size={12} /> Usage</span>
                            <div className="marketplace-usage-bar-wrap">
                              <div className="marketplace-usage-bar">
                                <div className="marketplace-usage-bar-fill" style={{ width: `${pct}%`, backgroundColor: color }} />
                              </div>
                              <span className="marketplace-usage-text" style={{ color }}>{pct}% ({l.total_requests}/{l.usage_limit})</span>
                            </div>
                          </div>
                        )
                      })()}
                    </div>
                  </>
                )}
                <div className="marketplace-card-actions">
                  <button className="marketplace-toggle-btn" onClick={() => handleToggle(l)}>
                    {l.is_active ? <><ToggleRight size={18} className="marketplace-icon-active" /> {t.leasingActive}</> : <><ToggleLeft size={18} className="marketplace-icon-paused" /> {t.leasingPaused}</>}
                  </button>
                  {editingId !== l.id && <button className="marketplace-edit-btn" onClick={() => startEdit(l)}><Edit2 size={16} /></button>}
                  <button className="marketplace-delete-btn" onClick={() => handleDelete(l.id)}><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        ))}

        {/* REQUESTS TAB */}
        {!loading && tab === 'requests' && (
          <div className="marketplace-requests">
            <div className="marketplace-requests-header">
              <p className="marketplace-requests-desc">{t.leasingRequestsDesc || 'Post what API you need. Others can accept or counter-offer.'}</p>
              <button className="marketplace-create-btn" onClick={() => setShowRequestForm(!showRequestForm)}>
                <Plus size={16} /> {t.leasingNewRequest || 'New Request'}
              </button>
            </div>

            {showRequestForm && (
              <form className="marketplace-create-form" onSubmit={handleCreateRequest}>
                <div className="marketplace-form-grid">
                  <label className="marketplace-field"><span><Tag size={14} /> Provider</span>
                    <select value={requestForm.provider_type} onChange={(e) => setRequestForm({ ...requestForm, provider_type: e.target.value })}>
                      {PROVIDER_OPTIONS.filter(p => p.value).map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select></label>
                  <label className="marketplace-field"><span><Activity size={14} /> Model</span>
                    <input type="text" value={requestForm.model} onChange={(e) => setRequestForm({ ...requestForm, model: e.target.value })} placeholder="gpt-4o..." required /></label>
                  <label className="marketplace-field"><span>Max tokens</span>
                    <input type="number" min="0" value={requestForm.max_tokens} onChange={(e) => setRequestForm({ ...requestForm, max_tokens: e.target.value })} placeholder="Optional" /></label>
                  <label className="marketplace-field"><span><DollarSign size={14} /> Budget ($)</span>
                    <input type="number" min="0" value={requestForm.budget_cents} onChange={(e) => setRequestForm({ ...requestForm, budget_cents: e.target.value })} placeholder="Optional" /></label>
                  <label className="marketplace-field"><span>Proposed input ($/1K)</span>
                    <input type="number" min="0" value={requestForm.proposed_price_input} onChange={(e) => setRequestForm({ ...requestForm, proposed_price_input: e.target.value })} placeholder="Optional" /></label>
                  <label className="marketplace-field"><span>Proposed output ($/1K)</span>
                    <input type="number" min="0" value={requestForm.proposed_price_output} onChange={(e) => setRequestForm({ ...requestForm, proposed_price_output: e.target.value })} placeholder="Optional" /></label>
                  <label className="marketplace-field"><span><Clock size={14} /> Valid until</span>
                    <input type="date" value={requestForm.valid_until} onChange={(e) => setRequestForm({ ...requestForm, valid_until: e.target.value })} /></label>
                  <label className="marketplace-field marketplace-field--wide"><span><MessageSquare size={14} /> Description</span>
                    <textarea value={requestForm.description} onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })} placeholder="What do you need?" rows={2} style={{ resize: 'vertical' }} /></label>
                </div>
                <button type="submit" className="marketplace-submit-btn"><Plus size={14} /> Post Request</button>
              </form>
            )}

            {apiRequests.length === 0 ? (
              <p className="marketplace-empty">{t.leasingNoRequests || 'No requests yet.'}</p>
            ) : (
              <div className="marketplace-request-list">
                {apiRequests.map((r) => (
                  <div key={r.id} className={`marketplace-request-card ${r.is_own ? 'marketplace-request-card--own' : ''}`}>
                    <div className="marketplace-request-main" onClick={() => setExpandedRequest(expandedRequest === r.id ? null : r.id)}>
                      <div className="marketplace-request-left">
                        <span className="marketplace-provider-badge">{r.provider_type}</span>
                        <span className="marketplace-model">{r.model}</span>
                        {r.is_own ? <span className="marketplace-own-tag">{t.leasingYourOffer}</span> : null}
                      </div>
                      <div className="marketplace-request-right">
                        <span className="marketplace-request-user">{r.user_name}</span>
                        {r.proposed_price_input ? <span className="marketplace-request-price">{formatPrice(r.proposed_price_input)}/{formatPrice(r.proposed_price_output || 0)}</span> : null}
                        {r.budget_cents ? <span className="marketplace-request-budget"><DollarSign size={12} /> {formatPrice(r.budget_cents)}</span> : null}
                        {expandedRequest === r.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </div>
                    {expandedRequest === r.id && (
                      <div className="marketplace-request-details">
                        {r.description && <p className="marketplace-request-desc">{r.description}</p>}
                        <div className="marketplace-request-meta">
                          {r.max_tokens ? <span>Max tokens: {r.max_tokens.toLocaleString()}</span> : null}
                          {r.valid_until ? <span>Until: {new Date(r.valid_until).toLocaleDateString()}</span> : null}
                          <span>Posted: {new Date(r.created_at).toLocaleDateString()}</span>
                        </div>
                        {r.counter_offer_status === 'pending' && (
                          <div className="marketplace-counter-info">
                            <strong>Counter-offer ({r.counter_offer_by_name}):</strong>
                            <span>{formatPrice(r.counter_offer_price_input || 0)} in / {formatPrice(r.counter_offer_price_output || 0)} out /1K</span>
                            {r.is_own && <button className="button-primary" onClick={() => api.acceptCounterOffer(r.id).then(fetchRequests)}><Check size={14} /> Accept</button>}
                          </div>
                        )}
                        {!r.is_own && r.status === 'open' && (
                          <div className="marketplace-request-actions">
                            <button className="marketplace-lease-btn" onClick={() => handleAcceptRequest(r.id)}><Check size={14} /> Accept &amp; Lease</button>
                            <div className="marketplace-counter-form">
                              <span className="marketplace-counter-label">Counter:</span>
                              <input type="number" min="0" placeholder="In $/1K" value={counterForm.price_input} onChange={(e) => setCounterForm({ ...counterForm, price_input: e.target.value })} />
                              <input type="number" min="0" placeholder="Out $/1K" value={counterForm.price_output} onChange={(e) => setCounterForm({ ...counterForm, price_output: e.target.value })} />
                              <button className="button-secondary" onClick={() => handleCounterOffer(r.id)} disabled={!counterForm.price_input || !counterForm.price_output}>Send</button>
                            </div>
                          </div>
                        )}
                        {r.is_own && r.status === 'open' && (
                          <div className="marketplace-request-actions">
                            <button className="button-danger" onClick={() => handleCancelRequest(r.id)}><X size={14} /> Cancel</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
